import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Inputmask from "inputmask";
import { ArrowLeft, ArrowRight, CalendarCheck, CalendarCheck2, CalendarIcon, Clock, Loader, MapPin, Save } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useOutletContext } from "react-router-dom";
import { Config } from "../../Config";
import { datePickerFormat, notify } from "../helpers";
import { storeAppointment } from "../../APIConnections/appointments";

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    border: "1px solid #D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 1.5px #16a34a" : "none",
    "&:hover": {
      borderColor: "#9CA3AF",
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "0.875rem",
    color: "#9CA3AF",
    fontWeight: "normal",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#E5E7EB" : "transparent",
    color: "#374151",
    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    marginTop: '-1px',
    border: "1px solid #D1D5DB",
    borderRadius: "0 0 0.375rem 0.375rem",
    zIndex: 50,
  }),
};

const Appointment = () => {
  const practiceData = useOutletContext();
  const [activeTab, setActiveTab] = useState(1);
  const [appointmentSubmitLoader, setAppointmentSubmitLoader] = useState(false);
  const { locations } = practiceData;
  const today = new Date();
  const locationsArray = useMemo(() => {
    return locations
      ? locations.map((location) => ({
          id: location.id,
          locations: location,
        }))
      : [];
  }, [locations]);


  const gridCols = locationsArray?.length === 1 
    ? "grid-cols-1" 
    : locationsArray?.length === 2 
    ? "grid-cols-1 md:grid-cols-2" 
    : locationsArray?.length >= 3 
    ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
    : "grid-cols-1";

  const defaultValue = {
    account_id: practiceData?.account_id,
    location_id: null,
    patient_type: "New Patient",
    appointment_time: '',
    preferred_date_1: '',
    preferred_date_2: '',
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    phone: '',
    email: '',
    reason_for_visit: '',
    insurance_name: '',
    insurance_id: '',
    comment: '',
  };

  const [formData, setFormData] = useState(defaultValue);

  useEffect(() => {
    if (locationsArray?.length > 0 && formData?.location_id === null) {
      setFormData({
        ...formData,
        location_id: locationsArray[0]?.id,
      });
    }
  }, [locationsArray, formData]);

  const handleLocationClick = (id) => {
    setFormData({
      ...formData,
      location_id: id,
    });
  };

  const changeActiveTab = (value) => {
    setActiveTab(value);
  }

  const handleSelectChange = (selectedOption, actionMeta) => {
    setFormData({
      ...formData,
      [actionMeta.name]: selectedOption ? selectedOption.value : '',
    });
  };
  
  const handleChangeDate = (name, value) => {
    setFormData({
      ...formData,
      [name]: datePickerFormat(value),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const phoneInputs = document.querySelectorAll(".us_phone");
      Inputmask({
        mask: "(999) 999-9999",
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAppointmentSubmitLoader(true);

    try {
      const response = await storeAppointment(formData);

      if (response?.detail) {
        notify('error', response?.detail);
      } else { 
        if(response?.data?.status === 'success'){
          notify("success", response?.data?.message);
          changeActiveTab(3);
        } else {
          notify("error", response?.data?.message);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setAppointmentSubmitLoader(false);
    }
  };

  const clearActiveAppointment = () => {
    setActiveTab(1);
    setAppointmentSubmitLoader(false);
    setFormData(defaultValue);
  }


  return (
    <>
      <Helmet>
        <title>{practiceData?.alt_account_name ? practiceData?.alt_account_name : practiceData?.account_name || Config?._AppName} | Appointment</title>
        <meta
          name="description"
          content="Patient appointments are a crucial aspect of modern healthcare delivery, facilitating the coordination of medical services and ensuring timely access to care."
        />
        <meta property="og:title" content={`${practiceData?.alt_account_name ? practiceData?.alt_account_name : practiceData?.account_name || Config?._AppName} | Appointment`} />
        <meta
          property="og:description"
          content="Patient appointments are a crucial aspect of modern healthcare delivery, facilitating the coordination of medical services and ensuring timely access to care."
        />
        <meta property="og:image" content={`${practiceData?.logo_link}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${practiceData?.alt_account_name ? practiceData?.alt_account_name : practiceData?.account_name || Config?._AppName} | Appointment`} />
        <meta
          name="twitter:description"
          content="Patient appointments are a crucial aspect of modern healthcare delivery, facilitating the coordination of medical services and ensuring timely access to care."
        />
        <meta name="twitter:image" content={`${practiceData?.logo_link}`} />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        <h2 className="text-lg md:text-xl lg:text-2xl font-medium text-center md:mb-1 lg:mb-2">{activeTab !== 3 ? 'Request An Appointment' : 'Thanks for submitting a request'}</h2>
        <p className="text-sm md:text-base text-center font-thin text-gray-500">{activeTab !== 3 ? 'How can we help you?' : `We will contact you shortly to confirm your request, ${formData?.first_name} ${formData?.last_name}`}</p>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between my-12 w-full">
          <div className="flex items-center w-full">
            <div className={`w-8 h-8 rounded-full ${(activeTab === 1 || activeTab === 2 || activeTab === 3) ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'} flex items-center justify-center font-semibold mx-2`}>1</div>
            <div className={`flex-grow h-0.5 ${(activeTab === 2 || activeTab === 3) ? 'bg-green-600' : 'bg-gray-300'}`}></div>
          </div>
          <div className="flex items-center w-full">
            <div className={`w-8 h-8 rounded-full ${(activeTab === 2 || activeTab === 3) ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'} flex items-center justify-center font-semibold mx-2`}>2</div>
            <div className={`flex-grow h-0.5 ${activeTab === 3 ? 'bg-green-600' : 'bg-gray-300'}`}></div>
          </div>
          <div className={`w-8 h-8 min-w-8 rounded-full ${activeTab === 3 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'} flex items-center justify-center font-semibold mx-2`}>3</div>
        </div>
      </div>

      {/* Appointment Form */}
      <div className="max-w-5xl mx-auto">
        <form onSubmit={handleSubmit} autoComplete="off">
          {activeTab === 1 && (
            <>
              {/* Patient Type */}
              <div className="mb-8">
                <label className="text-sm md:text-base block text-gray-600 mb-2 font-semibold">This appointment is for:</label>
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-12">
                  <label className={`flex items-center border-2 rounded p-4 ${appointmentSubmitLoader ? "cursor-not-allowed" : "cursor-pointer"} w-full md:min-h-20 ${formData?.patient_type === "New Patient" ? "border-green-500 bg-gray-100" : "border-gray-200 bg-gray-50"}`}>
                  <input
                    required
                    disabled={appointmentSubmitLoader ? true : false}
                    type="radio"
                    name="patient_type"
                    value="New Patient"
                    className="relative size-5 mr-2 shrink-0 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-green-600 checked:bg-green-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                    checked={formData?.patient_type === "New Patient"}
                    onChange={handleChange}
                  />
                    New Patient
                  </label>
                  <label className={`flex items-center border-2 rounded p-4 ${appointmentSubmitLoader ? "cursor-not-allowed" : "cursor-pointer"} w-full md:min-h-20 ${formData?.patient_type === "Returning Patient" ? "border-green-500 bg-gray-100" : "border-gray-200 bg-gray-50"}`}>
                    <input
                      required
                      disabled={appointmentSubmitLoader ? true : false}
                      type="radio"
                      name="patient_type"
                      value="Returning Patient"
                      className="relative size-5 mr-2 shrink-0 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-green-600 checked:bg-green-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                      checked={formData?.patient_type === "Returning Patient"}
                      onChange={handleChange}
                    />
                    Returning Patient
                  </label>
                </div>
              </div>

              {/* Location */}
              <div className="mb-12">
                <label className="text-sm md:text-base block text-gray-600 mb-2 font-semibold">Location:</label>

                <div className={`grid ${gridCols} gap-6 mb-6`}>
                  {locationsArray.length > 0 ? (
                    locationsArray.map((location, index) => (
                      <div 
                        key={index}
                        className={`relative border-2 rounded p-4 ${
                          formData?.location_id === location?.locations?.id ? "border-green-500 bg-gray-100" : "border-gray-200 bg-gray-50"
                        } ${appointmentSubmitLoader ? "cursor-not-allowed" : "cursor-pointer"}`}
                        onClick={() => {
                          if (!appointmentSubmitLoader) {
                            handleLocationClick(location?.locations.id);
                          }
                        }}
                      >
                        <p className="font-semibold">{location?.locations?.name}</p>
                        <p className="text-sm text-gray-500">{location?.locations?.address}</p>
                      </div>
                    ))
                  ) : (
                    <div className="border rounded p-4 bg-gray-50">
                      <p className="font-sm text-red-500">Location Not Found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Continue Button */}
              <div className="flex justify-end">
                {locationsArray.length > 0 && (
                  <button
                    type="button"
                    onClick={() => changeActiveTab(2)}
                    className="inline-flex items-center gap-x-2 rounded bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Continue
                    <ArrowRight aria-hidden="true" className="ml-1 -mr-1.5 size-5" />
                  </button>
                )}
              </div>
            </>
          )}
          {activeTab === 2 && (
            <>
              <div className="mb-4 md:mb-6">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Preferred Time of Day <span className="text-red-500">*</span></label>
                <Select
                  required
                  isDisabled={appointmentSubmitLoader}
                  name="appointment_time"
                  options={[
                    { value: "Anytime of the day", label: "Anytime of the day" },
                    { value: "Morning", label: "Morning" },
                    { value: "Afternoon", label: "Afternoon" },
                    { value: "Evening", label: "Evening" }
                  ]}
                  onChange={handleSelectChange}
                  placeholder="Select time of day..."
                  isSearchable={true}
                  value={
                    formData.appointment_time
                      ? { value: formData.appointment_time, label: formData.appointment_time }
                      : null
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                />
              </div>

              {/* Preferred Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Preferred Date <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 relative">
                    <DatePicker
                      required
                      disabled={appointmentSubmitLoader ? true : false}
                      name="preferred_date_1"
                      placeholderText="mm/dd/yyyy"
                      selected={formData.preferred_date_1}
                      className="col-start-1 row-start-1 block w-full rounded py-1.5 pl-10 pr-2.5 border border-gray-300 hover:border-gray-400 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-1 focus:outline-green-600 sm:pl-9"
                      onChange={(date) => handleChangeDate("preferred_date_1", date)}
                      minDate={today}
                    />
                    <CalendarIcon
                      aria-hidden="true"
                      className="pointer-events-none col-start-1 row-start-1 -ml-1 size-5 self-center text-green-500 absolute top-1/2 left-3 transform -translate-y-1/2"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Add a Second Preferred Date</label>
                  <div className="grid grid-cols-1 relative">
                    <DatePicker
                      disabled={appointmentSubmitLoader ? true : false}
                      name="preferred_date_2"
                      placeholderText="mm/dd/yyyy"
                      selected={formData.preferred_date_2}
                      className="col-start-1 row-start-1 block w-full rounded py-1.5 pl-10 pr-2.5 border border-gray-300 hover:border-gray-400 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600 sm:pl-9"
                      onChange={(date) => handleChangeDate("preferred_date_2", date)}
                      minDate={today}
                    />
                    <CalendarIcon
                      aria-hidden="true"
                      className="pointer-events-none col-start-1 row-start-1 -ml-1 size-5 self-center text-green-500 absolute top-1/2 left-3 transform -translate-y-1/2"
                    />
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">First Name <span className="text-red-500">*</span></label>
                  <input
                    required
                    type="text"
                    disabled={appointmentSubmitLoader ? true : false}
                    name="first_name"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Last Name <span className="text-red-500">*</span></label>
                  <input
                    required
                    type="text"
                    disabled={appointmentSubmitLoader ? true : false}
                    name="last_name"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Date of Birth <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 relative">
                    <DatePicker
                      required
                      name="dob"
                      disabled={appointmentSubmitLoader ? true : false}
                      placeholderText="mm/dd/yyyy"
                      selected={formData.dob}
                      className="col-start-1 row-start-1 block w-full rounded py-1.5 pl-10 pr-2.5 border border-gray-300 hover:border-gray-400 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600 sm:pl-9"
                      onChange={(date) => handleChangeDate("dob", date)}
                    />
                    <CalendarIcon
                      aria-hidden="true"
                      className="pointer-events-none col-start-1 row-start-1 -ml-1 size-5 self-center text-green-500 absolute top-1/2 left-3 transform -translate-y-1/2"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Gender <span className="text-red-500">*</span></label>
                  <Select
                    required
                    isDisabled={appointmentSubmitLoader}
                    name="gender"
                    options={[
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                      { value: "Other", label: "Other" }
                    ]}
                    onChange={handleSelectChange}
                    placeholder="Select your gender..."
                    isSearchable={true}
                    value={
                      formData.gender
                        ? { value: formData.gender, label: formData.gender }
                        : null
                    }
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={customSelectStyles}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Phone Number <span className="text-red-500">*</span></label>
                  <input
                    required
                    type="tel"
                    disabled={appointmentSubmitLoader ? true : false}
                    id="phone"
                    name="phone"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600 us_phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Email</label>
                  <input
                    type="email"
                    disabled={appointmentSubmitLoader ? true : false}
                    name="email"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Reason for Visit */}
              <div className="mb-2 md:mb-5">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Reason for Visit</label>
                <textarea
                  name="reason_for_visit"
                  disabled={appointmentSubmitLoader ? true : false}
                  className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                  rows="3"
                  placeholder="Enter reason..."
                  value={formData.reason_for_visit}
                  onChange={handleChange}
                ></textarea>
              </div>

              {/* Insurance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Insurance</label>
                  <input
                    type="text"
                    disabled={appointmentSubmitLoader ? true : false}
                    name="insurance_name"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                    value={formData.insurance_name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Insurance ID Number</label>
                  <input
                    type="text"
                    disabled={appointmentSubmitLoader ? true : false}
                    name="insurance_id"
                    className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                    value={formData.insurance_id}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Comments */}
              <div className="mb-10">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-medium">Comments</label>
                <textarea
                  name="comment"
                  disabled={appointmentSubmitLoader ? true : false}
                  className="w-full border border-gray-300 hover:border-gray-400 rounded px-2.5 py-1.5 text-gray-700 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-green-600"
                  rows="3"
                  placeholder="Please do not submit any Protected Health Information (PHI)"
                  value={formData.comment}
                  onChange={handleChange}
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => changeActiveTab(1)}
                  className="inline-flex items-center gap-x-2 rounded bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-4"
                >
                  <ArrowLeft aria-hidden="true" className="-ml-0.5 size-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={appointmentSubmitLoader ? true : false}
                  className="relative inline-flex items-center gap-x-2 rounded bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  <Save aria-hidden="true" className="-ml-0.5 size-5" />
                  Submit
                  {appointmentSubmitLoader && (
                    <span className="absolute top-0 left-0 z-40 w-full h-full flex items-center justify-center set-loader bg-green-500 rounded">
                      <Loader width={20} height={20} className="text-white" />
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
          {activeTab === 3 && (
            <>
              {/* Location */}
              <div className="mb-6">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-semibold">Location:</label>
                <div className="flex items-center bg-gray-100 p-4 rounded">
                  <MapPin className="w-6 h-7 text-green-500 mr-3" />
                  {locationsArray.length > 0 ? (
                    locationsArray.map((location, index) => (
                      <div key={index}>
                        {location?.locations?.id === formData?.location_id && (
                          <>
                            <p className="font-semibold">{location?.locations?.name}</p>
                            <p className="text-sm text-gray-500">{location?.locations?.address}</p>
                          </>
                        )}
                      </div>
                    ))
                  ) : (
                    <div>
                      <p className="font-sm text-red-500">Location Not Found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Preferred Time */}
              <div className="mb-6">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-semibold">Preferred Time of Day:</label>
                <div className="flex items-center bg-gray-100 p-4 rounded">
                  <Clock className="w-6 h-6 text-green-500 mr-3" />
                  <p>{formData?.appointment_time}</p>
                </div>
              </div>

              {/* Preferred Dates */}
              <div className="mb-12">
                <label className="text-sm md:text-base block text-gray-600 mb-1 font-semibold">Preferred Dates:</label>
                <div className="space-y-2">
                  <div className="flex items-center bg-gray-100 p-4 rounded">
                    <CalendarCheck2 className="w-6 h-6 text-green-500 mr-3" />
                    <p>{formData?.preferred_date_1}</p>
                  </div>
                  <div className="flex items-center bg-gray-100 p-4 rounded">
                    <CalendarCheck2 className="w-6 h-6 text-green-500 mr-3" />
                    <p>{formData?.preferred_date_2 || formData?.preferred_date_1}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => clearActiveAppointment()}
                  className="inline-flex items-center gap-x-2 rounded bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-4 cursor-pointer"
                >
                  <CalendarCheck aria-hidden="true" className="-ml-0.5 size-5" />
                  Book an Another Appointment
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
};

export default Appointment;
